import { Component, Inject } from '@angular/core'
import { MapService } from 'src/app/core/services/map.service'
import { Subject, debounceTime, interval, takeUntil } from 'rxjs'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { AppPaths } from 'src/app/app-routing.module'
import { Layers } from 'src/app/shared/layers-config/layers'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { getMetricFromMap } from '../enums/customer-metrics.enum'
import { LayerCustomerWarningService } from 'src/app/shared/layers/layer-customer-warning.service'
import { DataCustomerWarningService } from 'src/app/shared/services/data-customer-warning.service'
import { AppConfigModel } from 'src/app/core/models'
import { APP_CONFIG } from 'src/app/app.config'
import { Permissions } from 'src/app/core/enums/permissions.enum'
import { AuthService } from 'src/app/core/services/auth.service'
import { WarningDatasources } from 'src/app/shared/enums/warning-types.enum'
import { customerWarningLayerProvider } from 'src/app/shared/providers/warning-layers-provider'
import { LayerSimulationWarningService } from 'src/app/shared/layers/layer-simulation-warning.service'
import { DataSimulationWarningService } from 'src/app/shared/services/data-simulation-warning.service'

@Component({
  selector: 'app-customer-page',
  templateUrl: './customer-page.component.html',
  styleUrls: ['./customer-page.component.scss'],
  standalone: false,

  providers: [
    LayerCustomerWarningService,
    LayerSimulationWarningService,
    DataCustomerWarningService,
    DataSimulationWarningService,
    { provide: 'metrics', useValue: getMetricFromMap },
    customerWarningLayerProvider,
  ],
})
export class CustomerPageComponent {
  private stopTimer = new Subject<void>()

  public isInLiveMode = false
  public autoRefreshIntervalInMs = 0
  public displayRefreshInterval = 0
  public layerIds = Layers
  public currentTimestamp: Date | undefined
  public currentTimespan: number | undefined
  public warningId = ''
  public Permissions = Permissions
  public onDestroy: boolean = false
  public allowedFilters: WarningDatasources[] = []
  public currentFilter: string = ''
  public isHistoryVisible = false
  public isDownloadVisible = false

  constructor(
    @Inject(APP_CONFIG) public readonly config: AppConfigModel,
    private mapService: MapService,
    public appState: AppStateService,
    public AuthService: AuthService,
    @Inject('customerWarningLayer')
    public customerWarningLayerService: LayerCustomerWarningService,
    private simulationWarningLayerService: LayerSimulationWarningService,
  ) {
    this.allowedFilters = this.AuthService.getUserClaims().userDataFilters
    this.currentFilter = this.allowedFilters[0]
    this.mapService.activateGeoLocationSearch()
    this.mapService.setPosition(
      this.appState.getState().lat,
      this.appState.getState().lon,
      this.appState.getState().zoom,
    )
    if (this.appState.getState().historyEnabled) {
      this.isHistoryVisible = true
    }
    if (this.appState.getState().downloadEnabled) {
      this.isDownloadVisible = true
    }

    this.updateQueryParameters()
    this.mapService.mapViewChanged
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(1000))
      .subscribe(() => {
        this.updateQueryParameters()
      })

    this.evaluateQueryParameters()
    this.appState.stateChanged.pipe(takeUntilDestroyed()).subscribe(() => {})

    this.appState.hasWarningDatasourceChanged$.next(this.currentFilter as WarningDatasources)
  }

  ngOnDestroy() {
    this.mapService.deactivateGeoLocationSearch()
    this.stopTimer.next()
    this.stopTimer.complete()
  }

  setLiveModeRefreshInterval(seconds: number) {
    this.autoRefreshIntervalInMs = seconds * 1000
    this.displayRefreshInterval = seconds
    this.stopLiveMode()
    if (this.autoRefreshIntervalInMs > 0) {
      this.startLiveMode()
    }
  }

  startLiveMode() {
    this.isInLiveMode = true
    this.stopTimer = new Subject<void>()
    interval(this.autoRefreshIntervalInMs)
      .pipe(takeUntil(this.stopTimer))
      .subscribe(() => {
        this.customerWarningLayerService.reload()
        this.simulationWarningLayerService.reload()
      })
  }

  toggleHistory = () => {
    if (this.isDownloadVisible) {
      this.isDownloadVisible = false
    }

    // Disable refresh when entering history mode
    if (!this.isHistoryVisible) {
      this.setLiveModeRefreshInterval(0)
    }

    this.isHistoryVisible = !this.isHistoryVisible
    this.updateQueryParameters()
  }

  handleHistoryClosed = () => {
    // Disable refresh when closing history mode
    this.setLiveModeRefreshInterval(0)
    this.isHistoryVisible = false
    this.updateQueryParameters()
  }

  handleDownloadClosed = () => {
    this.isDownloadVisible = false
    this.updateQueryParameters()
  }

  stopLiveMode() {
    this.isInLiveMode = false
    this.stopTimer.next()
  }

  setCurrentFilter(filter: WarningDatasources) {
    this.currentFilter = filter
    this.appState.hasWarningDatasourceChanged$.next(filter)
  }

  evaluateQueryParameters() {}

  updateQueryParameters() {
    this.appState.setState(AppPaths.Customer, {
      lat: this.mapService.map.getCenter().lat,
      lon: this.mapService.map.getCenter().lng,
      zoom: this.mapService.map.getZoom(),
      historyEnabled: this.isHistoryVisible ? true : undefined,
      downloadEnabled: this.isDownloadVisible ? true : undefined,
    })
  }

  toggleDownload = () => {
    if (this.isHistoryVisible) {
      this.isHistoryVisible = false
    }
    this.isDownloadVisible = !this.isDownloadVisible
    this.updateQueryParameters()
  }
}
